import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "swiper/css/effect-fade";

import { useEffect, useState, useMemo } from "react";
import cn from "classnames";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import { Swiper, SwiperSlide, useSwiperSlide } from "swiper/react";

import { SwipeTracking } from "@/src/component/partial/SwipeTracking";
import { ProviderHooks, StyleHooks } from "@/src/hook";
import { FrontendTrackingService } from "@/src/service";
import { StrapiComponentType } from "@/src/type";
import { DynamicPageSection } from "@/src/type/strapi/collection";

import HeroCardsCarouselButtons from "./HeroCardsCarouselButtons";
import { GapBreakpointModule, ButtonLinkModule, ImageBreakpointModule, TextBreakpointModule } from "../../../../../../partial/__module__";

import styles from "./HeroCards.module.scss";

function autoScrollOptions(autoScrollOptions?: StrapiComponentType.Section.CarouselAutoScrollOptions) {
  switch (autoScrollOptions ?? "off") {
    case "lgAndBelow":
      return "lg";
    case "mdAndBelow":
      return "md";
    case "smAndBelow":
      return "sm";
    default:
      return "md";
  }
}

interface HeroCardsProps {
  cards: Array<StrapiComponentType.Object.HeroCard>;
  id: string;
  section: DynamicPageSection<StrapiComponentType.Section.Hero>;
  snapToGrid?: boolean;
  userAgent?: string;
  backgroundGradientOff?: boolean;
  hero: StrapiComponentType.Section.Hero;
  bottomDrawerHeight?: number;
  lazy?: "eager" | "lazy";
}

function HeroCards({ cards, id, userAgent, snapToGrid, backgroundGradientOff, hero, bottomDrawerHeight, section, lazy }: HeroCardsProps) {
  const swiperSlide = useSwiperSlide();
  const [swiper, setSwiper] = useState<any>(null);
  const autoScrollBreakpoint = StyleHooks.useBreakPointDown(autoScrollOptions(hero.autoScrollOptions));
  const cms = ProviderHooks.useCMS();
  const quality = cms?.webConfig?.defaultImageQuality ?? 100;
  const pagination = `hc-page-${id}`;
  const heroCardsId = `HeroCards-${hero.id}`;
  const modules = useMemo(() => {
    const _modules = [Navigation, Pagination, Autoplay];
    if (hero.transitionEffect === "fade") {
      _modules.push(EffectFade);
    }
    return _modules;
  }, [hero.transitionEffect]);
  const [loadImages, setLoadImages] = useState<boolean>(false);

  /**
   * Swiper uses class name to add required onClick for prev/next functionality
   * Set the max number of results (todo: move this to CMS global web config)
   */
  const [prevButton] = useState<string>(`hcprev_${id}`);
  const [nextButton] = useState<string>(`hcnext_${id}`);

  /**
   * this creates the pagination items for the bottom of the screen on mobile
   * Swiper does not accept the react/JSX way of rendering components and must return a string
   */
  const renderPaginationItem = (index: number, className: string) => {
    const itemStyles = cn(
      styles.paginationItem,
      hero?.paginationIconColor === "primary" ? styles.primary : styles.secondary,
      hero?.paginationIcon === "dots" ? styles.dots : undefined
    );
    return `<span class="${className} ${itemStyles}"></span>`;
  };

  /**
   * this determines based on cms settings when the carousel should auto scroll
   * https://swiperjs.com/swiper-api#autoplay
   */
  const autoScroll = useMemo(() => {
    const interval = hero.autoScrollIntervalMs ?? 3000;
    switch (hero.autoScrollOptions ?? "off") {
      case "lgAndBelow":
      case "mdAndBelow":
      case "smAndBelow":
        return autoScrollBreakpoint ? { delay: interval } : false;
      case "on":
        return { delay: interval };
      default:
        return false;
    }
  }, [hero, autoScrollBreakpoint]);

  /**
   * pause the auto swiper effect when carousel is not in focus
   */
  useEffect(() => {
    //don't trigger until swipper is fully mounted
    if (!swiper || typeof swiper?.destroy === "boolean" || !swiper?.autoplay) {
      return;
    }

    if (!autoScroll) {
      if (swiper?.running) {
        swiper?.autoplay.pause();
      }
      return;
    }

    if (!swiperSlide.isActive) {
      swiper?.autoplay.pause();
    } else if (!swiper?.running) {
      swiper?.autoplay.start();
      swiper?.autoplay.resume();
    }
  }, [swiperSlide.isActive, autoScroll, swiper]);

  if ((cards ?? []).length === 0) {
    return <></>;
  }

  function textPosition(textPosition?: string): string | undefined {
    switch (textPosition ?? "bottomLeft") {
      case "center":
        return styles.center;
      case "bottomCenter":
        return styles.bottomCenter;
      default:
        return;
    }
  }

  return (
    <>
      <SwipeTracking
        selector={`#swiper_${heroCardsId}`}
        onSwipe={() => {
          FrontendTrackingService.postTrackingLog({
            action: "swiped",
            actionId: `Hero:${hero.id}`,
            actionIdType: "componentName:componentId",
            actionMessage: `HeroCards section swiped with ${section.name} and index slide ${(swiper?.activeIndex ?? 0) + 1}`,
            triggeredFrom: "HeroCards.tsx:(SwipeTracking.tsx(onSwipe))",
          });
        }}
      />
      <Swiper
        id={`swiper_${heroCardsId}`}
        className={cn(styles.swiper, snapToGrid ? styles.snapToGrid : undefined)}
        effect={hero.transitionEffect}
        modules={modules}
        spaceBetween={0}
        slidesPerView={1}
        pagination={{
          el: `.${pagination}`,
          clickable: true,
          renderBullet: renderPaginationItem,
        }}
        loop={true}
        direction="horizontal"
        onSwiper={(swiper: any) => {
          setSwiper(swiper);
          if (typeof swiper?.slideToLoop === "function") {
            swiper?.slideToLoop(0);
          }
        }}
        onSlideChange={() => {
          setLoadImages(true);
        }}
        navigation={{
          prevEl: `#${prevButton}`,
          nextEl: `#${nextButton}`,
        }}
        autoplay={autoScroll}
        userAgent={userAgent}
      >
        <HeroCardsCarouselButtons
          prevClassName={prevButton}
          nextClassName={nextButton}
          classes={{
            prevClassName: styles.buttonPrev,
            nextClassName: styles.buttonNext,
          }}
          tracking={{
            action: "buttonClicked",
            actionId: `Hero:${hero.id}`,
            actionIdType: "componentName:componentId",
            actionMessage: `HeroCards section swiped with ${section.name} and index slide ${(swiper?.activeIndex ?? 0) + 1}`,
            triggeredFrom: "HeroCards.tsx:(HeroCardsCarouselButtons.tsx(tracking))",
          }}
        />
        {(cards ?? []).map((card, iter) => {
          const cardLazy = lazy === "lazy" ? "lazy" : iter === 0 ? "eager" : "lazy";
          return (
            <SwiperSlide
              key={card.id}
              className={cn(styles.card, textPosition(card.textPosition))}
            >
              <GapBreakpointModule
                id={`HeroCardsInner_${card.id}`}
                className={cn(styles.inner, textPosition(card.textPosition))}
                inlineStyle={{
                  paddingBottom:
                    (card.textPosition ?? "bottomLeft").includes("bottom") && bottomDrawerHeight ? `calc(${bottomDrawerHeight}px + 20px)` : undefined,
                  maxWidth: typeof card.textBoxWidth === "number" ? `${card.textBoxWidth}px` : undefined,
                }}
              >
                {card?.aboveTitleImage && (
                  <ImageBreakpointModule
                    id={`HeroCardsAboveTitleImage_${card.id}`}
                    stylePrefix={`HeroCardsAboveTitleImage_${card.id}`}
                    images={card.aboveTitleImage}
                    quality={quality}
                    classes={{
                      container: styles.aboveTitleImage,
                    }}
                    lazy={lazy}
                  />
                )}
                <div className={styles.titleContainer}>
                  {card?.title && (
                    <TextBreakpointModule
                      id={`HeroCardsTitle_${card.id}`}
                      stylePrefix={`HeroCardsTitle_${card.id}`}
                      text={card.title}
                      className={styles.title}
                    />
                  )}
                  {card?.nextToTile && (
                    <TextBreakpointModule
                      id={`HeroCardsNextToTitle_${card.id}`}
                      stylePrefix={`HeroCardsNextToTitle_${card.id}`}
                      text={card.nextToTile}
                      className={styles.nextToTitle}
                    />
                  )}
                </div>
                {card?.description && (
                  <TextBreakpointModule
                    id={`HeroCardsDescription_${card.id}`}
                    stylePrefix={`HeroCardsDescription_${card.id}`}
                    text={card.description}
                    className={styles.description}
                  />
                )}
                {card.cta && (
                  <div className={cn(styles.buttons, textPosition(card.textPosition), hero.stackCta ? styles.stackCta : undefined)}>
                    {(card.cta ?? []).map((cta, i) => (
                      <ButtonLinkModule
                        key={i}
                        buttonLink={cta}
                      />
                    ))}
                  </div>
                )}
              </GapBreakpointModule>
              {card?.imageBreakpoints && (
                <ImageBreakpointModule
                  id={`HeroCardsImage_${card.id}`}
                  stylePrefix={`HeroCardsImage_${card.id}`}
                  images={card.imageBreakpoints}
                  quality={quality}
                  classes={{
                    container: styles.image,
                  }}
                  lazy={loadImages ? "eager" : (cardLazy ?? "lazy")}
                />
              )}
              {!backgroundGradientOff && <div className={styles.backgroundGradient}></div>}
            </SwiperSlide>
          );
        })}
        <div className={cn(pagination, styles.pagination, hero.paginationIcon === "dots" ? styles.dots : undefined)}></div>
      </Swiper>
    </>
  );
}

export default HeroCards;
